import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
} from '@mui/material';

import { useIsViewer } from '../auth/hooks/useIsViewer';
import { Analyzer } from 'types';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';

interface Props {
  analyzer: Analyzer | undefined;
  snapshotId: number;
  openMonitorDialog: () => void;
  openThresholdDialog: () => void;
  openMaskDialog: () => void;
  handleBaselineClicked: (baselineEnabled: boolean | undefined) => void;
  handleCreateMonitorClicked: () => void;
  handleCreateThresholdClicked: () => void;
  handleCreateMaskClicked: () => void;
  handleClearClicked: (detector: 'Monitors' | 'Masks' | 'Thresholds') => void;
}

const TraceHistoryToolbar = ({
  analyzer,
  snapshotId,
  openMonitorDialog,
  openThresholdDialog,
  openMaskDialog,
  handleBaselineClicked,
  handleCreateMonitorClicked,
  handleCreateThresholdClicked,
  handleCreateMaskClicked,
  handleClearClicked,
}: Props) => {
  const isViewer = useIsViewer();

  const [monitorAnchorEl, setMonitorAnchorEl] = useState<null | Element>(null);
  const [maskAnchorEl, setMaskAnchorEl] = useState<null | Element>(null);
  const [traceAnchorEl, setTraceAnchorEl] = useState<null | Element>(null);

  const { data: snapshot } = useSnapshot({ snapshotId });

  const handleTraceMenuClicked = (event: React.MouseEvent) => {
    setTraceAnchorEl(event.currentTarget);
  };

  const handleTraceMenuClose = () => {
    setTraceAnchorEl(null);
  };

  const handleMonitorMenuClicked = (event: React.MouseEvent) => {
    setMonitorAnchorEl(event.currentTarget);
  };

  const handleMonitorMenuClose = () => {
    setMonitorAnchorEl(null);
  };

  const handleMaskMenuClicked = (event: React.MouseEvent) => {
    setMaskAnchorEl(event.currentTarget);
  };

  const handleMaskMenuClose = () => {
    setMaskAnchorEl(null);
  };

  const handleMonitorSettingsClicked = () => {
    openMonitorDialog();
    handleMonitorMenuClose();
  };

  const handleThresholdSettingsClicked = () => {
    openThresholdDialog();
    handleMonitorMenuClose();
  };

  const handleMaskSettingsClicked = () => {
    openMaskDialog();
    handleMaskMenuClose();
  };

  const baselineChecked = (event: React.MouseEvent) => {
    event.preventDefault();

    handleTraceMenuClose();
    handleBaselineClicked(snapshot?.baseline_enabled);
  };

  return (
    <Grid container item xs spacing={1}>
      <Grid item xs={3}>
        <Button
          onClick={handleTraceMenuClicked}
          size='large'
          fullWidth
          disabled={isViewer}
        >
          Trace
        </Button>
        <Menu
          id='trace-menu'
          anchorEl={traceAnchorEl}
          open={Boolean(traceAnchorEl)}
          onClose={handleTraceMenuClose}
        >
          <MenuItem onClick={baselineChecked}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!snapshot?.baseline_enabled}
                  name='baseline-checkbox'
                />
              }
              label='Baseline'
            />
          </MenuItem>
        </Menu>
      </Grid>
      {!isViewer && (
        <Grid item xs={3}>
          <Button onClick={handleMonitorMenuClicked} size='large' fullWidth>
            Monitors
          </Button>

          <Menu
            id='monitors-menu'
            anchorEl={monitorAnchorEl}
            open={Boolean(monitorAnchorEl)}
            onClose={handleMonitorMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleMonitorMenuClose();
                handleCreateMonitorClicked();
              }}
            >
              Create Monitors
            </MenuItem>
            <MenuItem onClick={handleMonitorSettingsClicked}>
              Monitor Settings
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClearClicked('Monitors');
                handleMonitorMenuClose();
              }}
            >
              Clear Monitors
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMonitorMenuClose();
                handleCreateThresholdClicked();
              }}
            >
              Create Threshold
            </MenuItem>
            <MenuItem onClick={handleThresholdSettingsClicked}>
              Threshold Settings
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClearClicked('Thresholds');
                handleMonitorMenuClose();
              }}
            >
              Clear Thresholds
            </MenuItem>
          </Menu>
        </Grid>
      )}
      {!isViewer && (
        <Grid item xs={3} style={{ minWidth: '11rem' }}>
          <Button onClick={handleMaskMenuClicked} size='large' fullWidth>
            Spectral Masks
          </Button>
          <Menu
            id='spectral-masks-menu'
            anchorEl={maskAnchorEl}
            open={Boolean(maskAnchorEl)}
            onClose={handleMaskMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleMaskMenuClose();
                handleCreateMaskClicked();
              }}
            >
              Create Spectral Mask
            </MenuItem>
            <MenuItem onClick={handleMaskSettingsClicked}>
              Spectral Mask Settings
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClearClicked('Masks');
                handleMaskMenuClose();
              }}
            >
              Clear Spectral Masks
            </MenuItem>
          </Menu>
        </Grid>
      )}
    </Grid>
  );
};

export default TraceHistoryToolbar;
